import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { Wallet, AlertCircle, Sparkles, TrendingDown, TrendingUp, RefreshCw, Play, Bell, Send } from "lucide-react";

const fmt = (n, ccy = "AUD", sym = "A$") => {
  if (n === null || n === undefined) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sym}${(n / 1_000).toFixed(1)}K`;
  return `${sym}${n.toFixed(0)}`;
};

const RISK_COLORS = {
  HIGH: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/5",
  MED: "text-[#FFB000] border-[#FFB000]/40 bg-[#FFB000]/5",
  LOW: "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/5",
};

export default function CashFlow() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [noSource, setNoSource] = useState(false);
  const [alertCfg, setAlertCfg] = useState(null);
  const [testing, setTesting] = useState(false);
  // Phase 35A — pickable source list
  const [sources, setSources] = useState([]);
  const [pickedSourceId, setPickedSourceId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [fc, ac, ss] = await Promise.all([
        api.get("/cashflow/latest"),
        api.get("/cashflow/alerts/config").catch(() => ({ data: null })),
        api.get("/cashflow/sources").catch(() => ({ data: [] })),
      ]);
      setData(fc.data || null);
      setAlertCfg(ac.data);
      setSources(ss.data || []);
      // Default selection: previously-used source if available, else the first ready source
      const lastId = fc.data?.source_id;
      const firstReady = (ss.data || []).find(s => s.ready);
      setPickedSourceId(lastId || firstReady?.id || "");
    } catch {
      setData(null);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const url = pickedSourceId
        ? `/cashflow/forecast?source_id=${encodeURIComponent(pickedSourceId)}`
        : "/cashflow/forecast";
      const r = await api.post(url);
      setData(r.data);
      setNoSource(false);
      toast.success("Cash-flow forecast ready.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Forecast failed";
      if (msg.toLowerCase().includes("no cash-flow")) setNoSource(true);
      toast.error(msg);
    }
    setRunning(false);
  };

  const toggleAlerts = async () => {
    const next = !alertCfg?.config?.enabled;
    await api.patch("/cashflow/alerts/config", { enabled: next });
    toast.success(next ? "Drift alerts enabled." : "Drift alerts paused.");
    load();
  };

  const setChannel = async (ev) => {
    const v = ev.target.value.trim();
    await api.patch("/cashflow/alerts/config", { slack_channel: v || null });
    toast.success("Channel saved.");
  };

  const testAlert = async () => {
    setTesting(true);
    try {
      await api.post("/cashflow/alerts/test");
      toast.success("Drift scan complete — check your Slack workspace.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
    setTesting(false);
  };

  const sym = data?.currency_symbol || "A$";
  const ccy = data?.currency || "AUD";

  return (
    <div className="p-6 space-y-6" data-testid="cashflow-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · FINANCIAL FORECAST</div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tighter uppercase mt-1">Cash-Flow Forecast</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Reads any cash-flow ready source — Xero, MYOB, or a mapped CSV — and projects expected
            collection across 30 / 60 / 90 days. Identifies risk customers and surfaces what-if scenarios.
            Powered by DARA Engine + deterministic AR curves.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {sources.length > 0 && (
            <select
              value={pickedSourceId}
              onChange={(e) => setPickedSourceId(e.target.value)}
              data-testid="cashflow-source-picker"
              className="bg-black border border-zinc-800 px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest text-zinc-300 focus:outline-none focus:border-[#FFB000]"
              title="Pick which data source drives the forecast"
            >
              <option value="">Auto-pick</option>
              {sources.map(s => (
                <option key={s.id} value={s.id} disabled={!s.ready}>
                  {s.ready ? "✓" : "○"} {s.name} · {s.origin}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={run}
            disabled={running}
            data-testid="cashflow-run-btn"
            style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }}
            className="font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
          >
            {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {running ? "FORECASTING…" : data ? "RE-RUN FORECAST" : "RUN FORECAST"}
          </button>
        </div>
      </div>

      {loading && <div className="font-mono text-[11px] text-zinc-500">Loading…</div>}

      {!loading && !data && noSource && (
        <div className="border border-[#FFB000]/30 bg-[#FFB000]/5 p-6 flex items-start gap-4">
          <AlertCircle className="w-5 h-5 text-[#FFB000] mt-1 shrink-0" />
          <div>
            <div className="font-mono text-[12px] uppercase tracking-widest text-[#FFB000]">
              NO CASH-FLOW READY SOURCE
            </div>
            <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
              Cash-flow forecasting needs either a Xero Invoices source or a CSV with the Amount + Due-Date
              columns mapped. Either{" "}
              <a href="/app/sources" className="text-[#FFB000] underline">connect Xero</a>{" "}
              or open a CSV source and set the cash-flow column mapping.
            </p>
          </div>
        </div>
      )}

      {!loading && !data && !noSource && (
        <Panel title="// NO FORECAST YET">
          <div className="p-8 text-center">
            <Wallet className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
            <p className="font-mono text-[11px] text-zinc-500">
              Click RUN FORECAST to generate your first projection.
            </p>
          </div>
        </Panel>
      )}

      {!loading && data && (
        <>
          {/* Narrative + headline KPIs */}
          <Panel title="// EXECUTIVE SUMMARY" meta={<span className="font-mono text-[9px] text-zinc-600">{new Date(data.created_at).toLocaleString()}</span>}>
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-[#FFB000] mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-200 leading-relaxed">{data.narrative}</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-zinc-900">
                <Kpi label="OUTSTANDING" value={fmt(data.ageing.total_outstanding, ccy, sym)} />
                <Kpi label="INVOICES" value={data.ageing.invoice_count.toLocaleString()} sub={`${data.ageing.overdue_invoice_count} overdue`} />
                <Kpi label="TOP-3 CONCENTRATION" value={`${data.ageing.concentration_top3_pct}%`} sub={data.ageing.concentration_top3_pct > 50 ? "⚠ Elevated" : "Healthy"} danger={data.ageing.concentration_top3_pct > 50} />
                <Kpi label="CONFIDENCE" value={`${data.projection.confidence_pct}%`} />
              </div>
            </div>
          </Panel>

          {/* Projection bars */}
          <Panel title="// COLLECTION PROJECTION" meta={<span className="font-mono text-[9px] text-zinc-600">{ccy}</span>}>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: "d30", label: "NEXT 30 DAYS", color: "#00E676" },
                { key: "d60", label: "DAYS 31-60", color: "#FFB000" },
                { key: "d90", label: "DAYS 61-90", color: "#FF3B30" },
              ].map(c => {
                const total = data.projection.d30 + data.projection.d60 + data.projection.d90;
                const pct = total ? (data.projection[c.key] / total) * 100 : 0;
                return (
                  <div key={c.key} className="border border-zinc-900 p-4">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{c.label}</div>
                    <div className="font-mono text-3xl text-white mt-2">{fmt(data.projection[c.key], ccy, sym)}</div>
                    <div className="h-1 bg-zinc-900 mt-3">
                      <div className="h-full" style={{ width: `${pct}%`, backgroundColor: c.color }} />
                    </div>
                    <div className="font-mono text-[10px] text-zinc-600 mt-2">{pct.toFixed(1)}% of 90-day collection</div>
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* AR ageing buckets */}
          <Panel title="// AR AGEING BUCKETS">
            <div className="p-5">
              <div className="grid grid-cols-5 gap-2">
                {[
                  { k: "current", label: "CURRENT", color: "#00E676" },
                  { k: "1-30", label: "1-30 DAYS", color: "#A8E600" },
                  { k: "31-60", label: "31-60", color: "#FFB000" },
                  { k: "61-90", label: "61-90", color: "#FF6B00" },
                  { k: "90+", label: "90+", color: "#FF3B30" },
                ].map(b => {
                  const v = data.ageing.buckets[b.k] || 0;
                  const pctOfTotal = data.ageing.total_outstanding ? (v / data.ageing.total_outstanding) * 100 : 0;
                  return (
                    <div key={b.k} className="border border-zinc-900 p-3">
                      <div className="font-mono text-[9px] text-zinc-500">{b.label}</div>
                      <div className="font-mono text-lg text-white mt-1">{fmt(v, ccy, sym)}</div>
                      <div className="h-0.5 mt-2" style={{ backgroundColor: b.color, width: `${Math.max(4, pctOfTotal)}%` }} />
                      <div className="font-mono text-[9px] text-zinc-600 mt-1">{pctOfTotal.toFixed(0)}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Panel>

          {/* Risk customers */}
          {data.risk_customers?.length > 0 && (
            <Panel title="// RISK CUSTOMERS">
              <div className="divide-y divide-zinc-900">
                {data.risk_customers.map((c, i) => (
                  <div key={i} data-testid={`risk-customer-${i}`} className="p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading font-bold text-sm text-white">{c.customer}</span>
                        <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${RISK_COLORS[c.risk_level] || RISK_COLORS.MED}`}>
                          {c.risk_level || "MED"} RISK
                        </span>
                        <span className="font-mono text-[10px] text-zinc-400">{fmt(c.total_due, ccy, sym)}</span>
                      </div>
                      <p className="text-sm text-zinc-400 mt-2"><span className="font-mono text-[9px] text-zinc-600 uppercase tracking-widest mr-2">WHY</span>{c.reason}</p>
                      <p className="text-sm text-[#00E676] mt-1"><span className="font-mono text-[9px] text-zinc-600 uppercase tracking-widest mr-2">NEXT</span>{c.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {/* What-if scenarios */}
          {data.what_if?.length > 0 && (
            <Panel title="// WHAT-IF SCENARIOS">
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-900">
                {data.what_if.map((s, i) => {
                  const key = Object.keys(s).find(k => k.startsWith("impact_"));
                  const impact = key ? s[key] : 0;
                  const positive = impact >= 0;
                  return (
                    <div key={i} className="p-5" data-testid={`whatif-${i}`}>
                      <div className="flex items-center gap-2">
                        {positive
                          ? <TrendingUp className="w-4 h-4 text-[#00E676]" />
                          : <TrendingDown className="w-4 h-4 text-[#FF3B30]" />}
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{s.label}</div>
                      </div>
                      <div className={`font-mono text-2xl mt-2 ${positive ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                        {positive ? "+" : ""}{fmt(impact, ccy, sym)}
                      </div>
                      <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{s.description}</p>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          {/* Top outstanding customers */}
          <Panel title="// TOP OUTSTANDING CUSTOMERS">
            <div className="divide-y divide-zinc-900">
              {data.ageing.top_customers.slice(0, 10).map((c, i) => (
                <div key={i} className="p-3 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] text-white truncate">{c.customer}</div>
                    <div className="font-mono text-[9px] text-zinc-600 mt-0.5">
                      {c.invoice_count} invoice{c.invoice_count === 1 ? "" : "s"}
                      {c.max_days_overdue > 0 && <span className="text-[#FFB000] ml-2">· {c.max_days_overdue}d overdue</span>}
                    </div>
                  </div>
                  <div className="font-mono text-sm text-[#FFB000] shrink-0">{fmt(c.total_due, ccy, sym)}</div>
                </div>
              ))}
            </div>
          </Panel>

          {/* Drift alerts config */}
          {alertCfg && (
            <Panel title="// DRIFT ALERTS · SLACK" meta={
              <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${alertCfg.slack_connected ? "text-[#00E676] border-[#00E676]/30" : "text-[#FFB000] border-[#FFB000]/30"}`}>
                {alertCfg.slack_connected ? `SLACK · ${alertCfg.slack_team || "CONNECTED"}` : "SLACK NOT CONNECTED"}
              </span>
            }>
              <div className="p-5 space-y-4">
                <p className="text-xs text-zinc-400 leading-relaxed max-w-3xl">
                  Every day at 07:00 UTC, DARA re-runs the forecast and pings your Slack channel when the numbers move materially — top-3 concentration drifts by more than {alertCfg.config.concentration_delta_pp}pp, total outstanding jumps &gt;{alertCfg.config.outstanding_jump_pct}%, or a customer crosses into the 61-90 / 90+ buckets.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    onClick={toggleAlerts}
                    data-testid="toggle-alerts-btn"
                    className={`border px-4 py-3 font-mono text-[11px] uppercase tracking-widest flex items-center gap-2 ${alertCfg.config.enabled ? "border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/5" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}
                  >
                    <Bell className={`w-3.5 h-3.5 ${alertCfg.config.enabled ? "" : "opacity-50"}`} />
                    {alertCfg.config.enabled ? "ALERTS ON" : "ALERTS PAUSED"}
                  </button>
                  <label className="block md:col-span-1">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Slack channel</span>
                    <input
                      data-testid="slack-channel-input"
                      defaultValue={alertCfg.config.slack_channel || ""}
                      onBlur={setChannel}
                      placeholder="#cash-alerts or U01234567"
                      className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                    />
                  </label>
                  <button
                    onClick={testAlert}
                    disabled={testing || !alertCfg.slack_connected}
                    data-testid="test-alert-btn"
                    title={!alertCfg.slack_connected ? "Connect Slack first (Settings → Advanced → Integrations)" : "Run drift scan now"}
                    className="border border-zinc-700 px-4 py-3 font-mono text-[11px] uppercase tracking-widest hover:border-[#FFB000]/50 hover:text-[#FFB000] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {testing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    {testing ? "TESTING…" : "SEND TEST NOW"}
                  </button>
                </div>

                {!alertCfg.slack_connected && (
                  <div className="border border-[#FFB000]/30 bg-[#FFB000]/5 px-3 py-2 font-mono text-[10px] text-[#FFB000]">
                    ⚠ Connect Slack first at <a href="/app/settings?tab=advanced" className="underline">Settings → Advanced → Integrations</a>. Once the bot is installed, enter a channel here and hit SEND TEST NOW.
                  </div>
                )}
              </div>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, danger }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-xl mt-1 ${danger ? "text-[#FF3B30]" : "text-white"}`}>{value}</div>
      {sub && <div className="font-mono text-[9px] text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
}
